/**
 * Sprint DS B12: Shortcut Overlay — press ? to show
 * Full-screen overlay with all shortcuts organized by category.
 */
import { useEffect } from "react";
import { X, Keyboard } from "lucide-react";
import { NAV_SHORTCUTS, CREATE_SHORTCUTS } from "@/hooks/use-keyboard-shortcuts";
import { INBOX_SHORTCUTS } from "@/hooks/use-inbox-shortcuts";

interface ShortcutOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

function ShortcutKey({ keys }: { keys: string }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {keys.split(" ").map((k, i) => (
        <kbd
          key={i}
          className="inline-flex items-center justify-center min-w-[24px] h-6 rounded bg-[var(--color-surface)] border border-[var(--color-border)] px-1.5 text-xs font-mono font-medium text-[var(--color-foreground)] shadow-sm"
        >
          {k.toUpperCase()}
        </kbd>
      ))}
    </span>
  );
}

export function ShortcutOverlay({ isOpen, onClose }: ShortcutOverlayProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-4xl rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Keyboard Shortcuts</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-[var(--color-surface)] text-[var(--color-muted-foreground)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-4">
          {/* Navigation */}
          <div>
            <h3 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide mb-3">Navigation</h3>
            <div className="space-y-2">
              {NAV_SHORTCUTS.map((s) => (
                <div key={s.key} className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-foreground)]">{s.label}</span>
                  <ShortcutKey keys={`G ${s.key}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div>
            <h3 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide mb-3">Actions</h3>
            <div className="space-y-2">
              {CREATE_SHORTCUTS.map((s) => (
                <div key={s.key} className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-foreground)]">{s.label}</span>
                  <ShortcutKey keys={`N ${s.key}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Inbox */}
          <div>
            <h3 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide mb-3">Inbox</h3>
            <div className="space-y-2">
              {INBOX_SHORTCUTS.map((s) => (
                <div key={s.key} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-[var(--color-foreground)]">{s.label}</span>
                  <ShortcutKey keys={s.key} />
                </div>
              ))}
            </div>
          </div>

          {/* Global */}
          <div>
            <h3 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide mb-3">Global</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--color-foreground)]">Command palette</span>
                <ShortcutKey keys="⌘ K" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--color-foreground)]">Focus mode</span>
                <ShortcutKey keys="⌘ ." />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--color-foreground)]">Focus AI bar</span>
                <ShortcutKey keys="⌘ J" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--color-foreground)]">This help</span>
                <ShortcutKey keys="?" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-[var(--color-border)] text-xs text-[var(--color-muted-foreground)]">
          Press <ShortcutKey keys="G" /> then a letter to navigate, or <ShortcutKey keys="N" /> then a letter to jump into a create flow without leaving the mounted shell.
        </div>
      </div>
    </div>
  );
}

/**
 * Small inline hint that appears when G or N is pressed.
 * Shows for 1.5s then disappears.
 */
export function ShortcutHint({ prefix, visible }: { prefix: "g" | "n" | null; visible: boolean }) {
  if (!visible || !prefix) return null;

  const label = prefix === "g" ? "Navigate to..." : "Create new...";

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[90] animate-in fade-in slide-in-from-bottom-2">
      <div className="rounded-lg bg-[var(--color-foreground)] text-[var(--color-background)] px-4 py-2 text-sm font-medium shadow-lg flex items-center gap-2">
        <kbd className="inline-flex items-center justify-center w-6 h-6 rounded bg-background/20 text-xs font-mono">
          {prefix.toUpperCase()}
        </kbd>
        {label}
      </div>
    </div>
  );
}
